
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Trophy, DollarSign, Award, Medal, Gift, Users, UserX, Crown, Star } from "lucide-react";

export type PrizeType = 'trofeo' | 'pala' | 'raqueta' | 'dinero' | 'certificado' | 'otro';

export interface Prize {
  type: PrizeType;
  description?: string; // For "otro" type
  amount?: number; // For "dinero" type
}

export interface CategoryPrizes {
  category: string;
  subcategory: string;
  firstPlace: Prize[];
  secondPlace?: Prize[];
}

interface PrizesDialogProps {
  eventTitle: string;
  sport: string;
  prizes: CategoryPrizes[];
  children: React.ReactNode;
  userCategory?: string; // Category + Subcategory combined (e.g., "Masculino 3era")
}

const PrizesDialog = ({ eventTitle, sport, prizes, children, userCategory }: PrizesDialogProps) => {
  const isTennis = sport.toLowerCase() === 'tenis' || sport.toLowerCase() === 'tennis';
  
  // Filter prizes if userCategory is provided
  const filteredPrizes = userCategory 
    ? prizes.filter(p => `${p.category} ${p.subcategory}` === userCategory)
    : prizes;
  const getCategoryHslColor = (category: string): string => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return 'hsl(217, 91%, 60%)';
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return 'hsl(315, 100%, 70%)';
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return 'hsl(142, 71%, 45%)';
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return 'hsl(45, 93%, 47%)';
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return 'hsl(0, 72%, 51%)';
    return 'hsl(142, 71%, 45%)';
  };

  const getCategoryLightHslColor = (category: string): string => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return 'hsl(217, 91%, 90%)';
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return 'hsl(315, 100%, 95%)';
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return 'hsl(142, 71%, 90%)';
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return 'hsl(45, 93%, 90%)';
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return 'hsl(0, 72%, 90%)';
    return 'hsl(142, 71%, 90%)';
  };

  const getCategoryIcon = (category: string) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return <Users className="h-4 w-4" />;
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return <Crown className="h-4 w-4" />;
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return <UserX className="h-4 w-4" />;
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return <Star className="h-4 w-4" />;
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return <Trophy className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  const getPrizeIcon = (type: PrizeType) => {
    switch (type) {
      case 'trofeo':
        return <Trophy className="h-4 w-4 text-prize-trophy" />;
      case 'pala':
      case 'raqueta':
        return <Award className="h-4 w-4 text-prize-racket" />;
      case 'dinero':
        return <DollarSign className="h-4 w-4 text-prize-money" />;
      case 'certificado':
        return <Medal className="h-4 w-4 text-prize-certificate" />;
      case 'otro':
        return <Gift className="h-4 w-4 text-prize-other" />;
      default:
        return <Trophy className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPrizeName = (prize: Prize) => {
    // Prefer the exact DB-provided description when available
    if (prize.description && prize.description.trim().length > 0) {
      return prize.description;
    }

    switch (prize.type) {
      case 'trofeo':
        return 'Trofeo';
      case 'pala':
        return 'Pala';
      case 'raqueta':
        return 'Raqueta';
      case 'dinero':
        return `$${prize.amount ?? 0}`;
      case 'certificado':
        return 'Certificado';
      case 'otro':
        return prize.description || 'Otro premio';
      default:
        return 'Premio';
    }
  };

  const renderPrizes = (prizeList: Prize[]) => {
    return (
      <div className="flex flex-wrap gap-2">
        {prizeList.map((prize, index) => (
          <Badge key={index} variant="secondary" className="flex items-center gap-1 bg-card border-2 border-muted hover:bg-muted/50">
            {getPrizeIcon(prize.type)}
            {getPrizeName(prize)}
          </Badge>
        ))}
      </div>
    );
  };

  // Group prizes by main category
  const groupPrizesByMainCategory = () => {
    const groupedPrizes: { [key: string]: CategoryPrizes[] } = {};
    
    filteredPrizes.forEach(categoryPrize => {
      const mainCategory = categoryPrize.category;
      if (!groupedPrizes[mainCategory]) {
        groupedPrizes[mainCategory] = [];
      }
      groupedPrizes[mainCategory].push(categoryPrize);
    });
    
    return groupedPrizes;
  };

  const groupedPrizes = groupPrizesByMainCategory();
  const mainCategories = Object.keys(groupedPrizes);

  const renderSubcategoryPrizes = (categoryPrize: CategoryPrizes) => {
    // Colors now applied via inline styles
    return (
      <div className="space-y-3">
        {/* First Place */}
        <div>
          <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
            <Medal className="h-4 w-4 text-prize-trophy" />
            Primer Lugar
          </h4>
          {renderPrizes(categoryPrize.firstPlace)}
        </div>
        
        {/* Second Place */}
        {categoryPrize.secondPlace && categoryPrize.secondPlace.length > 0 && (
          <div>
            <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
              <Medal className="h-4 w-4 text-muted-foreground" />
              Segundo Lugar
            </h4>
            {renderPrizes(categoryPrize.secondPlace)}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent 
        className="max-w-2xl max-h-[80vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-prize-trophy" />
            {sport.toLowerCase() === 'tenis' || sport.toLowerCase() === 'tennis' ? 'Premios' : `Premios - ${eventTitle}`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {mainCategories.length > 1 ? (
            <Tabs defaultValue={mainCategories[0]} className="w-full">
              <TabsList className="grid w-full bg-muted/30" style={{gridTemplateColumns: `repeat(${mainCategories.length}, minmax(0, 1fr))`}}>
                {mainCategories.map(category => (
                  <TabsTrigger 
                    key={category} 
                    value={category}
                    className="font-medium data-[state=active]:text-white"
                    style={{ '--tab-active-bg': getCategoryHslColor(category) } as React.CSSProperties}
                  >
                    <span className="flex items-center gap-1">
                      {getCategoryIcon(category)}
                      {category}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {mainCategories.map(category => (
                <TabsContent key={category} value={category} className="mt-4">
                  {isTennis ? (
              // Tennis: render prizes directly with subcategory label
                    <div className="space-y-4">
                      {groupedPrizes[category].map((categoryPrize, index) => (
                        <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(category) + '33', border: `1px solid ${getCategoryHslColor(category)}33` }}>
                          {categoryPrize.subcategory && (
                            <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                              {getCategoryIcon(categoryPrize.category)}
                              {categoryPrize.subcategory}
                            </h4>
                          )}
                          {renderSubcategoryPrizes(categoryPrize)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Accordion type="single" collapsible className="w-full">
                      {groupedPrizes[category].map((categoryPrize, index) => (
                        <AccordionItem key={index} value={`${category}-${index}`} className="border-muted/50">
                          <AccordionTrigger className="text-left px-4 py-3 rounded-lg mb-2 transition-colors" style={{ backgroundColor: getCategoryLightHslColor(category) }}>
                            <span className="font-medium text-base flex items-center gap-2">
                              {getCategoryIcon(categoryPrize.category)}
                              {categoryPrize.subcategory}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(category) + '33', border: `1px solid ${getCategoryHslColor(category)}33` }}>
                              {renderSubcategoryPrizes(categoryPrize)}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : mainCategories.length === 1 ? (
            <div>
              <h3 className="font-semibold text-lg mb-4 text-foreground flex items-center gap-2">
                {getCategoryIcon(mainCategories[0])}
                {mainCategories[0]}
              </h3>
              {isTennis ? (
                // Tennis: render prizes directly with subcategory label
                <div className="space-y-4">
                  {groupedPrizes[mainCategories[0]].map((categoryPrize, index) => (
                    <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(mainCategories[0]) + '33', border: `1px solid ${getCategoryHslColor(mainCategories[0])}33` }}>
                      {categoryPrize.subcategory && (
                        <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                          {getCategoryIcon(categoryPrize.category)}
                          {categoryPrize.subcategory}
                        </h4>
                      )}
                      {renderSubcategoryPrizes(categoryPrize)}
                    </div>
                  ))}
                </div>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {groupedPrizes[mainCategories[0]].map((categoryPrize, index) => (
                    <AccordionItem key={index} value={`single-${index}`} className="border-muted/50">
                      <AccordionTrigger className="text-left px-4 py-3 rounded-lg mb-2 transition-colors" style={{ backgroundColor: getCategoryLightHslColor(mainCategories[0]) }}>
                        <span className="font-medium text-base flex items-center gap-2">
                          {getCategoryIcon(categoryPrize.category)}
                          {categoryPrize.subcategory}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(mainCategories[0]) + '33', border: `1px solid ${getCategoryHslColor(mainCategories[0])}33` }}>
                          {renderSubcategoryPrizes(categoryPrize)}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No hay información de premios disponible para este evento.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrizesDialog;
